/**
 * VideoEditor — first-party lightweight video editor backed by FFmpeg.wasm.
 *
 * What this is:
 *   - Pick a source video from existing Media Library OR upload one
 *   - Trim (in/out seconds), pick a social format preset (16:9 / 9:16 / 1:1)
 *   - Overlay a single text caption + optional logo image
 *   - Render in-browser via FFmpeg.wasm
 *   - POST the rendered file to /api/video/projects/{id}/output → backend
 *     persists it under studio_recordings / video_projects and (next step)
 *     can be attached to a CMS page / sermon / campaign.
 *
 * What this is NOT:
 *   - A timeline editor with multiple tracks. That belongs to OpenCut once
 *     their Editor API is stable. We expose a project-status workflow so
 *     when the bridge ships, the same `video_projects` rows can be opened
 *     in OpenCut, edited, and the final output uploaded back here.
 *
 * Why FFmpeg.wasm (and not OpenCut-classic embed):
 *   - opencut-classic is archived and the new rewrite is pre-alpha.
 *   - FFmpeg.wasm is widely deployed, MIT-compatible, runs entirely in the
 *     operator's browser (no server GPU bill).
 *   - We still get trim / crop / overlay / social-format export which covers
 *     ~80% of church-media use cases (reels, sermon clips, highlights).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Video, Scissors, Upload, Sparkles, Trash2, Film, CheckCircle2, PlayCircle, BellRing, Download } from 'lucide-react';
import VideoReviewDialog from './VideoReviewDialog';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

const FFMPEG_CORE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';

export default function VideoEditor({ tenantId, siteId }) {
  const qc = useQueryClient();
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [reviewProjectId, setReviewProjectId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: presetsData } = useQuery({
    queryKey: ['video-presets'],
    queryFn: () => api.get('/video/presets').then(r => r.data),
  });
  const presets = presetsData?.presets || [];

  const { data: projectsData, refetch } = useQuery({
    queryKey: ['video-projects', tenantId],
    queryFn: () => api.get('/video/projects', { params: { tenant_id: tenantId } }).then(r => r.data),
    enabled: !!tenantId,
  });
  const projects = projectsData?.projects || [];
  const waiting = projects.filter(p => p.status === 'review');

  return (
    <div className="space-y-4" data-testid="video-editor">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Film className="h-4 w-4 text-fuchsia-600" /> Video Editor — Social Clips & Sermon Edits
          </CardTitle>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="video-new-project-btn">
            <Plus className="h-4 w-4 mr-1" /> New Project
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {waiting.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center gap-3"
                 data-testid="video-review-banner">
              <BellRing className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-900 min-w-0 flex-1">
                {waiting.length === 1
                  ? <><strong>{waiting[0].title}</strong> is finished and waiting for your review.</>
                  : <><strong>{waiting.length} videos</strong> are finished and waiting for your review.</>}
                <span className="block text-xs text-amber-700/80">
                  Watch it, approve it or ask for changes — and download it ready to post.
                </span>
              </p>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => setReviewProjectId(waiting[0].id)}
                      data-testid="video-review-banner-btn">
                <PlayCircle className="h-4 w-4 mr-1.5" /> Review {waiting.length > 1 ? 'the first' : 'now'}
              </Button>
            </div>
          )}
          {projects.length === 0 ? (
            <p className="text-sm text-slate-500 text-center border border-dashed rounded p-6" data-testid="video-empty-state">
              No video projects yet. Click <strong>New Project</strong> to start a sermon clip, reel, or highlight.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map(p => (
                <ProjectCard key={p.id} project={p} onOpen={() => setEditingProjectId(p.id)}
                             onReview={() => setReviewProjectId(p.id)} onChanged={refetch} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenantId={tenantId}
        siteId={siteId}
        presets={presets}
        onCreated={(id) => { setCreateOpen(false); setEditingProjectId(id); refetch(); }}
      />

      {editingProjectId && (
        <EditorWorkspace
          projectId={editingProjectId}
          presets={presets}
          onClose={() => { setEditingProjectId(null); refetch(); }}
          onSentForReview={(id) => { setEditingProjectId(null); setReviewProjectId(id); refetch(); }}
        />
      )}

      {reviewProjectId && (
        <VideoReviewDialog
          projectId={reviewProjectId}
          presets={presets}
          onClose={() => { setReviewProjectId(null); refetch(); }}
          onChanged={refetch}
        />
      )}
    </div>
  );
}

const STATUS_LABEL = {
  review: 'waiting for review',
  editing: 'in the editor',
  approved: 'approved',
  exported: 'exported',
  published: 'published',
  draft: 'draft',
  archived: 'archived',
};

function ProjectCard({ project, onOpen, onReview, onChanged }) {
  const delMut = useMutation({
    mutationFn: () => api.delete(`/video/projects/${project.id}`),
    onSuccess: () => { toast.success('Project deleted'); onChanged(); },
  });
  const statusColor = {
    draft: 'bg-slate-200 text-slate-700',
    editing: 'bg-amber-100 text-amber-800',
    review: 'bg-blue-100 text-blue-800',
    approved: 'bg-emerald-100 text-emerald-800',
    exported: 'bg-violet-100 text-violet-800',
    published: 'bg-fuchsia-600 text-white',
    archived: 'bg-slate-100 text-slate-500',
  }[project.status] || 'bg-slate-200';

  return (
    <div className="border rounded-md p-3 hover:shadow-sm transition" data-testid={`video-project-${project.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-sm truncate">{project.title}</h4>
        <Badge className={`text-[10px] ${statusColor}`} data-testid={`video-status-${project.id}`}>
          {STATUS_LABEL[project.status] || project.status}
        </Badge>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {project.social_format || 'no format'} · {project.output_files?.length || 0} output{(project.output_files?.length || 0) === 1 ? '' : 's'}
      </p>
      {project.status === 'review' && (
        <Button size="sm" className="w-full mt-2 bg-amber-600 hover:bg-amber-700"
                onClick={onReview} data-testid={`video-review-${project.id}`}>
          <PlayCircle className="h-3.5 w-3.5 mr-1" /> Review &amp; download
        </Button>
      )}
      {project.status !== 'review' && (project.output_files?.length || 0) > 0 && (
        <Button size="sm" variant="outline" className="w-full mt-2"
                onClick={onReview} data-testid={`video-watch-${project.id}`}>
          <Download className="h-3.5 w-3.5 mr-1" /> Watch &amp; download
        </Button>
      )}
      <div className="flex gap-1 mt-2">
        <Button size="sm" variant="outline" className="flex-1" onClick={onOpen} data-testid={`video-open-${project.id}`}>
          <Scissors className="h-3 w-3 mr-1" /> Edit
        </Button>
        <Button size="icon" variant="ghost" onClick={() => delMut.mutate()}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function CreateProjectDialog({ open, onClose, tenantId, siteId, presets, onCreated }) {
  const [form, setForm] = useState({ title: '', social_format: 'ig_reel_9_16', source_module: 'upload' });
  useEffect(() => { if (open) setForm({ title: '', social_format: 'ig_reel_9_16', source_module: 'upload' }); }, [open]);

  const mut = useMutation({
    mutationFn: () => api.post('/video/projects', {
      tenant_id: tenantId, site_id: siteId, title: form.title,
      social_format: form.social_format, source_module: form.source_module,
    }).then(r => r.data),
    onSuccess: (d) => onCreated(d.id),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="video-create-dialog">
        <DialogHeader><DialogTitle>New Video Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Sunday Sermon Reel" data-testid="video-new-title" />
          </div>
          <div>
            <Label className="text-xs">Social format preset</Label>
            <Select value={form.social_format} onValueChange={(v) => setForm({ ...form, social_format: v })}>
              <SelectTrigger data-testid="video-new-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={form.source_module} onValueChange={(v) => setForm({ ...form, source_module: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upload">Upload video</SelectItem>
                <SelectItem value="church_sermons">Existing Sermon</SelectItem>
                <SelectItem value="event">Event Recording</SelectItem>
                <SelectItem value="live_class">Live Class Recording</SelectItem>
                <SelectItem value="media_library">Media Library</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.title || mut.isPending} onClick={() => mut.mutate()} data-testid="video-new-create-btn">
            {mut.isPending ? 'Creating…' : 'Create & Edit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditorWorkspace({ projectId, presets, onClose, onSentForReview }) {
  const qc = useQueryClient();
  const { data: project, refetch } = useQuery({
    queryKey: ['video-project', projectId],
    queryFn: () => api.get(`/video/projects/${projectId}`).then(r => r.data),
  });

  const [sourceFile, setSourceFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [trimIn, setTrimIn] = useState(0);
  const [trimOut, setTrimOut] = useState(15);
  const [overlayText, setOverlayText] = useState('');
  const [progress, setProgress] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [outputUrl, setOutputUrl] = useState(null);

  const ffmpegRef = useRef(null);
  const videoRef = useRef(null);

  // Lazy-load FFmpeg.wasm
  const loadFFmpeg = async () => {
    if (ffmpegRef.current) return ffmpegRef.current;
    const ff = new FFmpeg();
    ff.on('progress', ({ progress }) => setProgress(Math.round(progress * 100)));
    await ff.load({
      coreURL: await toBlobURL(`${FFMPEG_CORE_URL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${FFMPEG_CORE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegRef.current = ff;
    return ff;
  };

  const preset = useMemo(() => presets.find(p => p.key === project?.social_format), [presets, project]);

  const handleRender = async () => {
    if (!sourceFile) { toast.error('Pick a source video first'); return; }
    if (trimOut <= trimIn) { toast.error('Trim out must be after trim in'); return; }
    setRendering(true); setProgress(0); setOutputUrl(null);
    try {
      const ff = await loadFFmpeg();
      await ff.writeFile('input.mp4', await fetchFile(sourceFile));

      // Build filter: scale + crop to preset + optional text overlay
      const w = preset?.width || 1080;
      const h = preset?.height || 1920;
      const filters = [
        `scale=${w}:${h}:force_original_aspect_ratio=increase`,
        `crop=${w}:${h}`,
      ];
      if (overlayText.trim()) {
        const esc = overlayText.replace(/'/g, "\\'").replace(/:/g, '\\:');
        filters.push(`drawtext=text='${esc}':fontcolor=white:fontsize=48:x=(w-text_w)/2:y=h-(text_h*2):box=1:boxcolor=black@0.6:boxborderw=20`);
      }

      const args = [
        '-ss', String(trimIn),
        '-i', 'input.mp4',
        '-t', String(trimOut - trimIn),
        '-vf', filters.join(','),
        '-c:v', 'libx264',
        '-preset', 'veryfast',
        '-pix_fmt', 'yuv420p',
        '-c:a', 'aac',
        '-b:a', '128k',
        'output.mp4',
      ];
      await ff.exec(args);
      const data = await ff.readFile('output.mp4');
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      setOutputUrl(URL.createObjectURL(blob));
      // Upload to backend
      const fd = new FormData();
      fd.append('file', blob, `output-${Date.now()}.mp4`);
      fd.append('label', `${preset?.label || 'output'} render`);
      if (preset?.key) fd.append('social_format', preset.key);
      await api.post(`/video/projects/${projectId}/output`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.post(`/video/projects/${projectId}/status`, { status: 'editing' }).catch(() => {});
      toast.success('Render complete — saved to project');
      refetch();
    } catch (e) {
      console.error(e);
      toast.error('Render failed: ' + (e.message || 'unknown'));
    } finally {
      setRendering(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setSourceFile(f);
    setSourceUrl(URL.createObjectURL(f));
    setTrimIn(0); setTrimOut(15);
  };

  return (
    <Dialog open={!!projectId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="video-editor-workspace">
        <DialogHeader>
          <DialogTitle>{project?.title || 'Loading…'} <Badge variant="outline" className="ml-2 text-[10px]">{project?.status}</Badge></DialogTitle>
        </DialogHeader>
        {!project ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            <div className="space-y-3">
              <div className="aspect-video bg-black rounded-md overflow-hidden flex items-center justify-center">
                {sourceUrl ? (
                  <video ref={videoRef} src={sourceUrl} controls className="w-full h-full" />
                ) : (
                  <p className="text-white/60 text-sm">Pick or upload a source video to start</p>
                )}
              </div>
              <Input type="file" accept="video/*" onChange={onPickFile} data-testid="video-source-file" />
              {outputUrl && (
                <div>
                  <Label className="text-xs">Render preview</Label>
                  <video src={outputUrl} controls className="w-full rounded-md mt-1 bg-black" data-testid="video-output-preview" />
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="border rounded-md p-3 space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1"><Scissors className="h-4 w-4" /> Trim</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">In (s)</Label>
                    <Input type="number" min={0} value={trimIn} onChange={(e) => setTrimIn(Number(e.target.value))} data-testid="video-trim-in" />
                  </div>
                  <div>
                    <Label className="text-xs">Out (s)</Label>
                    <Input type="number" min={0} value={trimOut} onChange={(e) => setTrimOut(Number(e.target.value))} data-testid="video-trim-out" />
                  </div>
                </div>
              </div>
              <div className="border rounded-md p-3 space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-1"><Sparkles className="h-4 w-4" /> Caption overlay</Label>
                <Input value={overlayText} onChange={(e) => setOverlayText(e.target.value)} placeholder="e.g. John 3:16" data-testid="video-overlay-text" />
              </div>
              <div className="border rounded-md p-3 space-y-1">
                <Label className="text-xs">Output preset</Label>
                <p className="text-sm font-medium">{preset?.label}</p>
                <p className="text-xs text-slate-500">{preset?.width}×{preset?.height} · {preset?.aspect}</p>
              </div>
              <Button disabled={!sourceFile || rendering} onClick={handleRender} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="video-render-btn">
                {rendering ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" /> Rendering… {progress}%</>) : (<><Video className="h-4 w-4 mr-1" /> Render & Save</>)}
              </Button>
              {project?.output_files?.length > 0 && (
                <div className="border rounded-md p-3">
                  <Label className="text-xs flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Saved outputs</Label>
                  <div className="text-xs space-y-1 mt-1">
                    {project.output_files.map(o => (
                      <div key={o.id} className="flex justify-between border-b py-0.5">
                        <span className="truncate">{o.label || o.filename}</span>
                        <span className="text-slate-400">{Math.round((o.size_bytes || 0) / 1024 / 1024)} MB</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button size="sm" variant="outline" className="flex-1"
                            onClick={() => api.post(`/video/projects/${projectId}/status`, { status: 'review' })
                              .then(() => {
                                toast.success('Sent for review — opening it now', {
                                  description: 'Watch it, approve it or ask for changes, and download it ready to post.',
                                });
                                onSentForReview?.(projectId);
                              })
                              .catch((e) => toast.error(getErrorMessage(e)))}
                            data-testid="video-submit-review-btn">
                      Submit for review
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
